import { Descriptions, Empty, Tag } from 'antd';
import {
  cardTypeLabel,
  genderLabel,
  identificationStatusColors,
  nationalityLabel,
} from 'apps/investment/constants/user';
import { Link } from 'react-router-dom';
import { formatDate } from 'utils/formatDate';
import FieldWarning from './field-warning';
import { AutoKycResponse } from './types';

const { Item: DescItem } = Descriptions;

interface IndividualIdentificationDetailProps {
  userIdentification: any;
  autoKyc?: AutoKycResponse;
  showLinks?: boolean; // Option to show/hide navigation links (useful in modal)
}

const IndividualIdentificationDetail = ({
  userIdentification,
  autoKyc,
  showLinks = true,
}: IndividualIdentificationDetailProps) => {
  if (!userIdentification)
    return <Empty description="Chưa có thông tin xác thực" />;

  const fieldWarnings: Record<string, string> = autoKyc?.field_warnings || {};

  const isResidentialV2 = userIdentification.residential_address_version === 'v2';
  const isAddressV2 = userIdentification.address_version === 'v2';

  // Helper to render warning for a field
  const renderWarning = (fieldName: string) => {
    if (!fieldWarnings || !(fieldName in fieldWarnings)) return null;
    return <FieldWarning message={fieldWarnings[fieldName]} />;
  };

  const CityComponent = ({ city, cityString }) => {
    if (showLinks && city) {
      return (
        <Link to={`/admin/provinces/${city.id}`}>
          {city.name || city.id}
        </Link>
      );
    }
    return city?.name || cityString || '-';
  };

  const DistrictComponent = ({ district, districtString, districtId }) => {
    if (showLinks && district) {
      return (
        <Link to={`/admin/districts/${district.id}`}>
          {district.name || '-'}
        </Link>
      );
    }
    return district?.name || districtString || districtId || '-';
  };

  const WardComponent = ({ ward, wardString }) => {
    if (showLinks && ward) {
      return (
        <Link to={`/admin/wards/${ward.id}`}>{ward.name || ward.id}</Link>
      );
    }
    return ward?.name || wardString || '-';
  };

  return (
    <>
      <Descriptions column={1} bordered size="small">
        <DescItem label="Trạng thái xác thực">
          <Tag
            color={
              identificationStatusColors[userIdentification.verification_status]
            }
          >
            {userIdentification.verification_status_label}
          </Tag>
        </DescItem>
        <DescItem label="Quốc tịch">
          {nationalityLabel[userIdentification.nationality] ?? '-'}
          {renderWarning('nationality')}
        </DescItem>
        <DescItem label="Họ và tên">
          {userIdentification.full_name}
          {renderWarning('full_name')}
        </DescItem>
        <DescItem label="Ngày sinh">
          {formatDate(userIdentification.date_of_birth)}
          {renderWarning('date_of_birth')}
        </DescItem>
        <DescItem label="Giới tính">
          {genderLabel[userIdentification.gender] ?? '-'}
          {renderWarning('gender')}
        </DescItem>
        <DescItem label="Loại căn cước">
          {cardTypeLabel[userIdentification.card_type]}
        </DescItem>
        <DescItem label="Số CMND/CCCD">
          {userIdentification.id_number}
          {renderWarning('id_number')}
        </DescItem>
        <DescItem label="Ngày cấp">
          {formatDate(userIdentification.issue_date)}
          {renderWarning('issue_date')}
        </DescItem>
        <DescItem label="Ngày hết hạn">
          {formatDate(userIdentification.expiry)}
          {renderWarning('expiry')}
        </DescItem>
        <DescItem label="Nơi cấp">
          {userIdentification.issue_place}
          {renderWarning('issue_place')}
        </DescItem>
        <DescItem label="Quê quán">
          {userIdentification.original_address}
          {renderWarning('original_address')}
        </DescItem>
      </Descriptions>

      <Descriptions size="small" column={1} bordered>
        <DescItem
          label={'Địa chỉ thường trú'}
          labelStyle={{ fontWeight: 'bold' }}
        >
          (residential_address)
        </DescItem>
        <DescItem label="Phiên bản">
          <Tag color={isResidentialV2 ? 'blue' : 'orange'}>
            {isResidentialV2
              ? 'Địa chỉ mới (Phường/Xã + Tỉnh/Thành Phố)'
              : 'Địa chỉ cũ'}
          </Tag>
        </DescItem>
        <DescItem label="Thành phố/Tỉnh">
          <CityComponent
            city={userIdentification.residential_address_city}
            cityString={userIdentification.residential_address_city_string}
          />
        </DescItem>
        {!isResidentialV2 && (
          <DescItem label="Quận/Huyện">
            <DistrictComponent
              district={userIdentification.residential_address_district}
              districtString={
                userIdentification.residential_address_district_string
              }
              districtId={userIdentification.residential_address_district?.id}
            />
          </DescItem>
        )}
        {isResidentialV2 && (
          <DescItem label="Phường/Xã">
            <WardComponent
              ward={userIdentification.residential_address_ward}
              wardString={userIdentification.residential_address_ward_string}
            />
          </DescItem>
        )}
        <DescItem label="Địa chỉ">
          {userIdentification.residential_address}
          {renderWarning('residential_address')}
        </DescItem>
        <DescItem label="Địa chỉ đầy đủ">
          <strong>{userIdentification.full_residential_address || '-'}</strong>
        </DescItem>

        <DescItem label={'Địa chỉ liên hệ'} labelStyle={{ fontWeight: 'bold' }}>
          (address)
        </DescItem>
        <DescItem label="Phiên bản">
          <Tag color={isAddressV2 ? 'blue' : 'orange'}>
            {isAddressV2
              ? 'Địa chỉ mới (Phường/Xã + Tỉnh/Thành Phố)'
              : 'Địa chỉ cũ'}
          </Tag>
        </DescItem>
        <DescItem label="Thành phố/Tỉnh">
          <CityComponent
            city={userIdentification.address_city}
            cityString={userIdentification.address_city_string}
          />
        </DescItem>
        {!isAddressV2 && (
          <DescItem label="Quận/Huyện">
            <DistrictComponent
              district={userIdentification.address_district}
              districtString={userIdentification.address_district_string}
              districtId={userIdentification.address_district?.id}
            />
          </DescItem>
        )}
        {isAddressV2 && (
          <DescItem label="Phường/Xã">
            <WardComponent
              ward={userIdentification.address_ward}
              wardString={userIdentification.address_ward_string}
            />
          </DescItem>
        )}
        <DescItem label="Địa chỉ">
          {userIdentification.address}
          {renderWarning('address')}
        </DescItem>
        <DescItem label="Địa chỉ đầy đủ">
          <strong>{userIdentification.full_address || '-'}</strong>
        </DescItem>
      </Descriptions>
    </>
  );
};

export default IndividualIdentificationDetail;
